import { supabase } from '../lib/supabase';
import { traduzirErroAdmin } from '../lib/erroAdmin';
import type { PlanoEmpresa } from '../types';

export interface PlanoConfig {
  slug:          PlanoEmpresa;
  nome:          string;
  preco_mensal:  number;
  limite_telas:  number;
  jukebox_ativo: boolean;
}

export type PlanoConfigAdmin = PlanoConfig & { id: string };

export type PlanoConfigUpdate = Pick<
  PlanoConfig,
  'nome' | 'preco_mensal' | 'limite_telas' | 'jukebox_ativo'
>;

const FALLBACK_PLANOS: PlanoConfig[] = [
  { slug: 'bronze', nome: 'Bronze', preco_mensal: 99,  limite_telas: 1, jukebox_ativo: false },
  { slug: 'prata',  nome: 'Prata',  preco_mensal: 249, limite_telas: 1, jukebox_ativo: true  },
  { slug: 'ouro',   nome: 'Ouro',   preco_mensal: 499, limite_telas: 1, jukebox_ativo: true  },
];

export const planosService = {
  async listar(): Promise<PlanoConfig[]> {
    const { data, error } = await supabase
      .from('planos_config')
      .select('slug, nome, preco_mensal, limite_telas, jukebox_ativo')
      .order('preco_mensal', { ascending: true });

    // Erro real não pode ser mascarado pelo fallback (jukebox e MRR com valores fixos).
    // Lista vazia, sim: um tenant não tem SELECT em planos_config e recebe 0 linhas sem erro.
    if (error) throw error;
    if (!data?.length) return FALLBACK_PLANOS;
    return data as PlanoConfig[];
  },

  /** Lista completa com id — lança se a tabela não existir (admin Settings). */
  async listarAdmin(): Promise<PlanoConfigAdmin[]> {
    const { data, error } = await supabase
      .from('planos_config')
      .select('*')
      .order('preco_mensal', { ascending: true });

    if (error) throw error;
    return (data ?? []) as PlanoConfigAdmin[];
  },

  async atualizar(id: string, payload: PlanoConfigUpdate): Promise<void> {
    const { error } = await supabase.rpc('admin_atualizar_plano_config' as never, {
      p_id: id, p_nome: payload.nome, p_preco_mensal: payload.preco_mensal,
      p_limite_telas: payload.limite_telas, p_jukebox_ativo: payload.jukebox_ativo,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async mapaPrecos(): Promise<Record<PlanoEmpresa, number>> {
    const planos = await planosService.listar();
    const mapa = {} as Record<PlanoEmpresa, number>;
    for (const p of planos) {
      mapa[p.slug] = Number(p.preco_mensal);
    }
    return mapa;
  },

  async buscarPorSlug(slug: PlanoEmpresa): Promise<PlanoConfig | null> {
    const planos = await planosService.listar();
    return planos.find(p => p.slug === slug) ?? null;
  },
};
